import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Chip, alpha, Tooltip, Paper, Drawer, Divider, IconButton } from '@mui/material';
import {
  ShowChart, Warning, Thermostat, LocalFireDepartment,
  AcUnit, Close,
  ElectricBolt, Compress, TrendingUp, MyLocation,
  Search, GpsFixed, Flag, SwapVert,
} from '@mui/icons-material';

// ── Tiny helpers ──────────────────────
const fmt = (v, d = 1) => (v != null && isFinite(v)) ? Number(v).toFixed(d) : '-';
const fmtK = (v) => (v != null && isFinite(v)) ? Math.round(v).toLocaleString('vi-VN') : '-';
const pct = (v, d = 1) => (v != null && isFinite(v)) ? `${Number(v) >= 0 ? '+' : ''}${Number(v).toFixed(d)}%` : '-';
const retColor = (v) => v > 0 ? '#10b981' : v < 0 ? '#ef4444' : '#64748b';

const ScoreBar = ({ score, maxScore = 2 }) => {
  const pctWidth = Math.min(100, Math.max(0, (score / maxScore) * 100));
  const color = score >= 1.2 ? '#10b981' : score >= 0.8 ? '#3b82f6' : score >= 0.5 ? '#f59e0b' : '#ef4444';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ flex: 1, height: 5, bgcolor: '#f1f5f9', borderRadius: 2, overflow: 'hidden', maxWidth: 50 }}>
        <Box sx={{ height: '100%', width: `${pctWidth}%`, bgcolor: color, borderRadius: 2, transition: 'width 0.6s ease-out' }} />
      </Box>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color, fontFamily: 'monospace', minWidth: 28 }}>
        {fmt(score, 2)}
      </Typography>
    </Box>
  );
};

const TradeTypeBadge = ({ type }) => {
  const { t } = useTranslation();
  const isSwing = type === 'SWING';
  return (
    <Tooltip title={isSwing ? t('regimeWatchlist.tradeType.swingTip') : t('regimeWatchlist.tradeType.tplusTip')} arrow>
    <Chip
      label={type}
      size="small"
      sx={{
        height: 18,
        fontSize: '0.55rem',
        fontWeight: 800,
        bgcolor: isSwing ? alpha('#8b5cf6', 0.12) : alpha('#f97316', 0.12),
        color: isSwing ? '#7c3aed' : '#ea580c',
        letterSpacing: 0.3,
        '& .MuiChip-label': { px: 0.6 },
      }}
    />
    </Tooltip>
  );
};

const TrapBadge = ({ flags }) => {
  if (!flags) return null;
  const flagList = flags.split(',').filter(Boolean);
  if (flagList.length === 0) return null;
  return (
    <Tooltip title={flags} arrow>
      <Warning sx={{ fontSize: 14, color: '#f59e0b' }} />
    </Tooltip>
  );
};

// ── Overheat indicator ──
const OVERHEAT_FLAG_KEYS = {
  'EXTENDED': { key: 'regimeWatchlist.overheat.EXTENDED', color: '#ef4444' },
  'RSI_OB':  { key: 'regimeWatchlist.overheat.RSI_OB', color: '#dc2626' },
  'STREAK':  { key: 'regimeWatchlist.overheat.STREAK', color: '#f97316' },
  'HOT_5D':  { key: 'regimeWatchlist.overheat.HOT_5D', color: '#ef4444' },
  'VOL_CLIMAX': { key: 'regimeWatchlist.overheat.VOL_CLIMAX', color: '#b91c1c' },
};

const OverheatMeter = ({ score, flags, distMa20, rsi14, consecutiveUp }) => {
  const { t } = useTranslation();
  const ohColor = score >= 60 ? '#dc2626' : score >= 40 ? '#f97316' : score >= 20 ? '#eab308' : '#22c55e';
  const Icon = score >= 60 ? LocalFireDepartment : score >= 20 ? Thermostat : AcUnit;

  const flagList = (flags || '').split('|').filter(Boolean);
  const tooltipLines = [
    t('regimeWatchlist.overheat.score', { score: fmt(score, 0) }),
    distMa20 != null ? t('regimeWatchlist.overheat.distMa20', { value: pct(distMa20) }) : null,
    rsi14 != null ? t('regimeWatchlist.overheat.rsi', { value: fmt(rsi14, 0) }) : null,
    consecutiveUp > 0 ? t('regimeWatchlist.overheat.consecutiveUp', { count: consecutiveUp }) : null,
    ...flagList.map(f => OVERHEAT_FLAG_KEYS[f] ? t(OVERHEAT_FLAG_KEYS[f].key) : f),
  ].filter(Boolean);

  return (
    <Tooltip title={<Box>{tooltipLines.map((l, i) => <div key={i}>{l}</div>)}</Box>} arrow>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, justifyContent: 'center' }}>
        <Icon sx={{ fontSize: 13, color: ohColor }} />
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: ohColor, fontFamily: 'monospace' }}>
          {fmt(score, 0)}
        </Typography>
      </Box>
    </Tooltip>
  );
};

// ═══════════════════════════════════════════════════════════
// Main WatchlistTable
// ═══════════════════════════════════════════════════════════

const WatchlistTable = ({ watchlist = [], regime, overheatSummary }) => {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState('score');
  const [sortAsc, setSortAsc] = useState(false);
  const [drawerItem, setDrawerItem] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!drawerItem) return undefined;

    // Mount first, then open on the next frame so Drawer can play
    // its enter transition instead of appearing instantly.
    const rafId = window.requestAnimationFrame(() => {
      setDrawerOpen(true);
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [drawerItem]);

  const sorted = useMemo(() => {
    const items = [...watchlist];
    items.sort((a, b) => {
      const av = a[sortKey] ?? 0;
      const bv = b[sortKey] ?? 0;
      return sortAsc ? av - bv : bv - av;
    });
    if (searchQuery) {
      const q = searchQuery.toUpperCase();
      return items.filter(item => item.symbol.includes(q));
    }
    return items;
  }, [watchlist, sortKey, sortAsc, searchQuery]);

  const handleSort = (key) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(false); }
  };

  const columns = [
    { key: 'symbol',         label: t('regimeWatchlist.col.symbol'),    width: 'minmax(70px, 1.3fr)',  align: 'left',   tip: t('regimeWatchlist.tip.symbol') },
    { key: 'overheat_score', label: t('regimeWatchlist.col.overheat'),  width: 'minmax(44px, 0.7fr)',  align: 'center', tip: t('regimeWatchlist.tip.overheat') },
    { key: 'score',          label: t('regimeWatchlist.col.score'),     width: 'minmax(80px, 1.5fr)',  align: 'center', tip: t('regimeWatchlist.tip.score') },
    { key: 'trade_type',     label: t('regimeWatchlist.col.tradeType'), width: 'minmax(56px, 0.9fr)',  align: 'center', tip: t('regimeWatchlist.tip.tradeType') },
    { key: 'close',          label: t('regimeWatchlist.col.price'),     width: 'minmax(62px, 1.1fr)',  align: 'right',  tip: t('regimeWatchlist.tip.price') },
    { key: 'dist_ma20_pct',  label: t('regimeWatchlist.col.ma20pct'),   width: 'minmax(52px, 0.9fr)',  align: 'right',  tip: t('regimeWatchlist.tip.ma20pct') },
    { key: 'rsi14',          label: t('regimeWatchlist.col.rsi'),       width: 'minmax(40px, 0.7fr)',  align: 'center', tip: t('regimeWatchlist.tip.rsi') },
    { key: 'stop',           label: t('regimeWatchlist.col.stop'),      width: 'minmax(62px, 1.1fr)',  align: 'right',  tip: t('regimeWatchlist.tip.stop') },
    { key: 'target',         label: t('regimeWatchlist.col.target'),    width: 'minmax(62px, 1.1fr)',  align: 'right',  tip: t('regimeWatchlist.tip.target') },
    { key: 'rr',             label: t('regimeWatchlist.col.rr'),        width: 'minmax(40px, 0.7fr)',  align: 'center', tip: t('regimeWatchlist.tip.rr') },
    { key: 'ret_5d',         label: t('regimeWatchlist.col.ret5d'),     width: 'minmax(56px, 1fr)',    align: 'right',  tip: t('regimeWatchlist.tip.ret5d') },
    { key: 'ret_20d',        label: t('regimeWatchlist.col.ret20d'),    width: 'minmax(56px, 1fr)',    align: 'right',  tip: t('regimeWatchlist.tip.ret20d') },
    { key: 'trap_flags',     label: '',                                 width: 'minmax(24px, 0.4fr)',  align: 'center' },
  ];

  const gridCols = columns.map(c => c.width).join(' ');

  if (watchlist.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: '1px solid #e5e7eb', borderRadius: 1 }}>
        <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>
          {t('regimeWatchlist.empty', { regime })}
        </Typography>
      </Paper>
    );
  }

  return (
    <>
    <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
    <Paper elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 1, overflow: 'hidden', minWidth: 680 }}>

      {/* Search bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.4, borderBottom: '1px solid #f1f5f9', bgcolor: 'white' }}>
        <Search sx={{ fontSize: 14, color: '#94a3b8', mr: 0.5 }} />
        <input
          placeholder={t('regimeWatchlist.search')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            border: 'none', outline: 'none', fontSize: '0.72rem',
            color: '#334155', background: 'transparent', width: 100,
            fontFamily: 'inherit',
          }}
        />
        {searchQuery && (
          <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', ml: 'auto' }}>
            {sorted.length}/{watchlist.length}
          </Typography>
        )}
      </Box>

      {/* Header row */}
      <Box sx={{
        display: 'grid', gridTemplateColumns: gridCols,
        px: 1.5, py: 0.6,
        bgcolor: '#f1f5f9', borderBottom: '1px solid #e2e8f0',
      }}>
        {columns.map(col => (
          <Tooltip key={col.key} title={col.tip || ''} arrow placement="top" disableHoverListener={!col.tip}>
          <Typography
            onClick={() => col.key !== 'trap_flags' && handleSort(col.key)}
            sx={{
              fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: 0.3, color: sortKey === col.key ? '#3b82f6' : '#64748b',
              textAlign: col.align, cursor: col.key !== 'trap_flags' ? 'pointer' : 'default',
              userSelect: 'none',
              '&:hover': col.key !== 'trap_flags' ? { color: '#3b82f6' } : {},
            }}
          >
            {col.label} {sortKey === col.key && (sortAsc ? '↑' : '↓')}
          </Typography>
          </Tooltip>
        ))}
      </Box>

      {/* Data rows */}
      {sorted.map((item, idx) => {
        return (
        <Box
          key={`${item.symbol}-${idx}`}
          onClick={() => {
            setDrawerOpen(false);
            setDrawerItem(item);
          }}
          sx={{
            display: 'grid', gridTemplateColumns: gridCols,
            px: 1.5, py: 0.65, alignItems: 'center',
            borderBottom: '1px solid #f1f5f9',
            transition: 'background 0.1s',
            cursor: 'pointer',
            '&:hover': { bgcolor: alpha('#3b82f6', 0.04) },
            ...(idx % 2 === 1 ? { bgcolor: '#fafbfc' } : {}),
          }}
        >
          {/* Symbol */}
          <Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#1e293b' }}>
                {item.symbol}
              </Typography>
              {item.company_name && (
                <Typography sx={{ fontSize: '0.5rem', color: '#64748b', fontWeight: 500, maxWidth: 100, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {item.company_name}
                </Typography>
              )}
          </Box>

          {/* Overheat meter */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <OverheatMeter
              score={item.overheat_score || 0}
              flags={item.overheat_flags}
              distMa20={item.dist_ma20_pct}
              rsi14={item.rsi14}
              consecutiveUp={item.consecutive_up}
            />
          </Box>

          {/* Score */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <ScoreBar score={item.score} />
          </Box>

          {/* Trade type */}
          <Box sx={{ textAlign: 'center' }}><TradeTypeBadge type={item.trade_type} /></Box>

          {/* Close */}
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e293b', textAlign: 'right', fontFamily: 'monospace' }}>
            {fmtK(item.close)}
          </Typography>

          {/* Distance from MA20 */}
          <Typography sx={{
            fontSize: '0.68rem', fontWeight: 700, textAlign: 'right', fontFamily: 'monospace',
            color: Math.abs(item.dist_ma20_pct || 0) > 8 ? '#ef4444' : Math.abs(item.dist_ma20_pct || 0) > 5 ? '#f59e0b' : '#64748b',
          }}>
            {pct(item.dist_ma20_pct)}
          </Typography>

          {/* RSI */}
          <Typography sx={{
            fontSize: '0.68rem', fontWeight: 800, textAlign: 'center', fontFamily: 'monospace',
            color: (item.rsi14 || 50) > 75 ? '#ef4444' : (item.rsi14 || 50) > 65 ? '#f59e0b' : (item.rsi14 || 50) < 30 ? '#3b82f6' : '#64748b',
          }}>
            {fmt(item.rsi14 || 50, 0)}
          </Typography>

          {/* Stop */}
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#ef4444', textAlign: 'right', fontFamily: 'monospace' }}>
            {fmtK(item.stop)}
          </Typography>

          {/* Target */}
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#10b981', textAlign: 'right', fontFamily: 'monospace' }}>
            {fmtK(item.target)}
          </Typography>

          {/* R:R */}
          <Typography sx={{
            fontSize: '0.78rem', fontWeight: 800, textAlign: 'center', fontFamily: 'monospace',
            color: item.rr >= 2 ? '#10b981' : item.rr >= 1.5 ? '#3b82f6' : '#f59e0b',
          }}>
            {fmt(item.rr, 1)}
          </Typography>

          {/* Ret 5d */}
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textAlign: 'right', fontFamily: 'monospace', color: retColor(item.ret_5d) }}>
            {pct(item.ret_5d)}
          </Typography>

          {/* Ret 20d */}
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textAlign: 'right', fontFamily: 'monospace', color: retColor(item.ret_20d) }}>
            {pct(item.ret_20d)}
          </Typography>

          {/* Trap */}
          <Box sx={{ textAlign: 'center' }}><TrapBadge flags={item.trap_flags} /></Box>
        </Box>
        );
      })}

      {/* Footer */}
      <Box sx={{ px: 2, py: 1, bgcolor: '#f8fafc', borderTop: '1px solid #e5e7eb' }}>
        <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8' }}>
          {t('regimeWatchlist.footer')}
        </Typography>
      </Box>
    </Paper>
    </Box>

    {/* ═══ Stock Detail Drawer ═══ */}
    <StockDetailDrawer
      item={drawerItem}
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      onExited={() => setDrawerItem(null)}
    />
    </>
  );
};

// ═══════════════════════════════════════════════════════════
// Stock Detail Drawer
// ═══════════════════════════════════════════════════════════

const StockDetailDrawer = ({ item, open, onClose, onExited }) => {
  const { t } = useTranslation();
  if (!item) return null;

  const stopPct = item.close > 0 ? ((item.close - item.stop) / item.close * 100) : 0;
  const targetPct = item.close > 0 ? ((item.target - item.close) / item.close * 100) : 0;
  const ma50Pct = item.ma50 > 0 ? ((item.close - item.ma50) / item.ma50 * 100) : 0;

  const scoreColor = item.score >= 1.2 ? '#10b981' : item.score >= 0.8 ? '#3b82f6' : item.score >= 0.5 ? '#f59e0b' : '#ef4444';
  const ohColor = (item.overheat_score || 0) >= 60 ? '#dc2626' : (item.overheat_score || 0) >= 40 ? '#f97316' : (item.overheat_score || 0) >= 20 ? '#eab308' : '#22c55e';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      transitionDuration={{ enter: 320, exit: 240 }}
      ModalProps={{ keepMounted: true }}
      SlideProps={{
        easing: {
          enter: 'cubic-bezier(0.22, 1, 0.36, 1)',
          exit: 'cubic-bezier(0.4, 0, 0.2, 1)',
        },
        onExited,
      }}
      slotProps={{
        backdrop: {
          timeout: { enter: 260, exit: 180 },
          sx: {
            bgcolor: 'rgba(15, 23, 42, 0.22)',
            backdropFilter: 'blur(4px)',
          },
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 420, md: 460 },
          bgcolor: '#fafbfc',
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.18)',
          backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(248,250,252,1) 100%)',
          borderLeft: '1px solid rgba(148, 163, 184, 0.16)',
          willChange: 'transform',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 42, height: 42, borderRadius: 1.5, bgcolor: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
            <Typography sx={{ fontWeight: 900, color: '#fff', fontSize: '0.9rem' }}>{item.symbol}</Typography>
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.8 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: '#1e293b' }}>{item.symbol}</Typography>
              <TradeTypeBadge type={item.trade_type} />
            </Box>
            {item.company_name && (
              <Typography sx={{ fontSize: '0.72rem', color: '#334155', mt: 0.1, fontWeight: 500 }}>{item.company_name}</Typography>
            )}
            {item.sector_code && (
              <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', mt: 0.1 }}>{item.sector_code}</Typography>
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small"><Close /></IconButton>
      </Box>
      <Divider />

      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Price + Score header */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography sx={{ fontSize: '1.8rem', fontWeight: 900, color: '#1e293b', fontFamily: 'monospace' }}>
              {fmtK(item.close)}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
              {[
                { label: 'MA20', ok: item.close > (item.ma20 || 0) },
                { label: 'MA50', ok: item.close > (item.ma50 || 0) },
              ].map(({ label, ok }) => (
                <Typography key={label} sx={{ fontSize: '0.62rem', fontWeight: 700, color: ok ? '#10b981' : '#ef4444', bgcolor: ok ? alpha('#10b981', 0.08) : alpha('#ef4444', 0.08), px: 0.6, py: 0.2, borderRadius: 0.8 }}>
                  {ok ? '▲' : '▼'}{label}
                </Typography>
              ))}
            </Box>
          </Box>

          {/* Key metrics grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1px', bgcolor: '#e2e8f0', borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
            {[
              { label: 'Score', value: fmt(item.score, 2), color: scoreColor },
              { label: 'R:R', value: fmt(item.rr, 1), color: item.rr >= 2 ? '#10b981' : item.rr >= 1.5 ? '#3b82f6' : '#f59e0b' },
              { label: 'Overheat', value: `${fmt(item.overheat_score || 0, 0)}/100`, color: ohColor },
              { label: 'RSI(14)', value: fmt(item.rsi14 || 50, 0), color: (item.rsi14 || 50) > 75 ? '#ef4444' : (item.rsi14 || 50) > 65 ? '#f59e0b' : '#64748b' },
              { label: 'Ret 5D', value: pct(item.ret_5d), color: retColor(item.ret_5d) },
              { label: 'Ret 20D', value: pct(item.ret_20d), color: retColor(item.ret_20d) },
            ].map(({ label, value, color }) => (
              <Box key={label} sx={{ bgcolor: '#fff', px: 1.5, py: 1 }}>
                <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</Typography>
                <Typography sx={{ fontSize: '1rem', fontWeight: 900, color, fontFamily: 'monospace', lineHeight: 1.3 }}>{value}</Typography>
              </Box>
            ))}
          </Box>

          {/* Trade Plan */}
          <Box sx={{ borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <Box sx={{ px: 1.5, py: 0.8, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <GpsFixed sx={{ fontSize: 14, color: '#6366f1' }} />
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569' }}>{t('regimeWatchlist.drawer.tradePlan')}</Typography>
            </Box>
            <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
              {/* Entry zone */}
              {item.entry_zone && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.8 }}>
                  <MyLocation sx={{ fontSize: 14, color: '#6366f1', mt: 0.2 }} />
                  <Box>
                    <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>{t('regimeWatchlist.drawer.entryZone')}</Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: '#334155', fontWeight: 600 }}>{item.entry_zone}</Typography>
                  </Box>
                </Box>
              )}
              {/* Stop / Target bar */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px', bgcolor: '#e2e8f0', borderRadius: 1.5, overflow: 'hidden' }}>
                <Box sx={{ bgcolor: alpha('#ef4444', 0.04), px: 1.5, py: 0.8 }}>
                  <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 600 }}>STOP-LOSS</Typography>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: '#ef4444', fontFamily: 'monospace' }}>
                    {fmtK(item.stop)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: '#ef4444', fontFamily: 'monospace' }}>
                    {pct(-Math.abs(stopPct))}
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: alpha('#10b981', 0.04), px: 1.5, py: 0.8 }}>
                  <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 600 }}>TARGET</Typography>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: '#10b981', fontFamily: 'monospace' }}>
                    {fmtK(item.target)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: '#10b981', fontFamily: 'monospace' }}>
                    {pct(targetPct)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* MA Context */}
          <Box sx={{ borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <Box sx={{ px: 1.5, py: 0.8, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <ShowChart sx={{ fontSize: 14, color: '#3b82f6' }} />
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569' }}>{t('regimeWatchlist.drawer.technicalPosition')}</Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1px', bgcolor: '#e2e8f0' }}>
              <Box sx={{ bgcolor: '#fff', px: 1.5, py: 1 }}>
                <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 600 }}>MA20</Typography>
                <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: '#1e293b', fontFamily: 'monospace' }}>{fmtK(item.ma20)}</Typography>
                <Typography sx={{ fontSize: '0.62rem', color: retColor(item.dist_ma20_pct), fontFamily: 'monospace', fontWeight: 700 }}>{pct(item.dist_ma20_pct)}</Typography>
              </Box>
              <Box sx={{ bgcolor: '#fff', px: 1.5, py: 1 }}>
                <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 600 }}>MA50</Typography>
                <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: '#1e293b', fontFamily: 'monospace' }}>{fmtK(item.ma50)}</Typography>
                <Typography sx={{ fontSize: '0.62rem', color: retColor(ma50Pct), fontFamily: 'monospace', fontWeight: 700 }}>{pct(ma50Pct)}</Typography>
              </Box>
            </Box>
          </Box>

          {/* Signal chips */}
          <Box sx={{ borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <Box sx={{ px: 1.5, py: 0.8, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <Flag sx={{ fontSize: 14, color: '#8b5cf6' }} />
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569' }}>{t('regimeWatchlist.drawer.signals')}</Typography>
            </Box>
            <Box sx={{ p: 1.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {item.breakout && (
                <Chip icon={<ElectricBolt sx={{ fontSize: 12 }} />} label="Breakout" size="small"
                  sx={{ height: 24, fontSize: '0.65rem', fontWeight: 700, bgcolor: alpha('#f97316', 0.08), color: '#ea580c', '& .MuiChip-icon': { ml: 0.3 } }} />
              )}
              {item.tight_contraction && (
                <Chip icon={<Compress sx={{ fontSize: 12 }} />} label={t('regimeWatchlist.signal.tightContraction')} size="small"
                  sx={{ height: 24, fontSize: '0.65rem', fontWeight: 700, bgcolor: alpha('#6366f1', 0.08), color: '#4f46e5', '& .MuiChip-icon': { ml: 0.3 } }} />
              )}
              {item.close > (item.ma20 || 0) && (
                <Chip icon={<TrendingUp sx={{ fontSize: 12 }} />} label={t('regimeWatchlist.signal.aboveMa20')} size="small"
                  sx={{ height: 24, fontSize: '0.65rem', fontWeight: 700, bgcolor: alpha('#10b981', 0.08), color: '#059669', '& .MuiChip-icon': { ml: 0.3 } }} />
              )}
              {item.close > (item.ma20 || 0) && item.ma20 > (item.ma50 || 0) && (
                <Chip icon={<SwapVert sx={{ fontSize: 12 }} />} label="MA20 > MA50" size="small"
                  sx={{ height: 24, fontSize: '0.65rem', fontWeight: 700, bgcolor: alpha('#3b82f6', 0.08), color: '#2563eb', '& .MuiChip-icon': { ml: 0.3 } }} />
              )}
              {item.overheat_flags && item.overheat_flags.split('|').filter(Boolean).map(f => (
                <Chip key={f} label={OVERHEAT_FLAG_KEYS[f] ? t(OVERHEAT_FLAG_KEYS[f].key) : f} size="small"
                  sx={{ height: 24, fontSize: '0.65rem', fontWeight: 700, bgcolor: alpha('#ef4444', 0.08), color: '#dc2626' }} />
              ))}
              {item.trap_flags && item.trap_flags.split(',').filter(Boolean).map(f => (
                <Chip key={f} icon={<Warning sx={{ fontSize: 12 }} />} label={f} size="small"
                  sx={{ height: 24, fontSize: '0.65rem', fontWeight: 700, bgcolor: alpha('#f59e0b', 0.08), color: '#d97706', '& .MuiChip-icon': { ml: 0.3 } }} />
              ))}
              {!item.breakout && !item.tight_contraction && !item.overheat_flags && !item.trap_flags && (
                <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>{t('regimeWatchlist.drawer.noSignals')}</Typography>
              )}
            </Box>
          </Box>

          {/* Selection reason */}
          {item.selection_reason && (
            <Box sx={{ borderRadius: 2, bgcolor: alpha('#6366f1', 0.04), border: '1px solid', borderColor: alpha('#6366f1', 0.15), px: 1.5, py: 1 }}>
              <Typography sx={{ fontSize: '0.58rem', color: '#6366f1', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, mb: 0.3 }}>
                {t('regimeWatchlist.drawer.selectionReason')}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#334155', fontWeight: 500, lineHeight: 1.5 }}>
                {item.selection_reason}
              </Typography>
            </Box>
          )}

        </Box>
      </Box>
    </Drawer>
  );
};

export default WatchlistTable;
